import { cn } from "@/utils/class-name";
import type { HandleProps } from "./use-panel-resize";

type GripOrientation = "horizontal" | "vertical";

export type GripOffset = number | `${number}%`;

interface GripLayout {
    lengthPaddingPx: number;
    crossPaddingPx: number;
}

const GRIP_LAYOUT: Record<GripOrientation, GripLayout> = {
    horizontal: { lengthPaddingPx: 12, crossPaddingPx: 6 },
    vertical: { lengthPaddingPx: 12, crossPaddingPx: 2 },
};

const GRIP_BAR_CLUSTER_CROSS_PX = 8;

function gripCrossSizePx(orientation: GripOrientation): number {
    return GRIP_BAR_CLUSTER_CROSS_PX + GRIP_LAYOUT[orientation].crossPaddingPx * 2;
}

export const PANEL_WIDTH_GRIP_RESERVE_PX = gripCrossSizePx("vertical");

function GripBar({ orientation }: { orientation: GripOrientation }) {
    return (
        <div
            className={cn("rounded-full bg-[#E4E5E2] group-hover:bg-[#B8B9B6] group-active:bg-[#A2A3A0]", {
                "h-8 w-0.5": orientation === "vertical",
                "h-0.5 w-8": orientation === "horizontal",
            })}
        />
    );
}

export function ResizerGrip({
    orientation,
    handleProps,
    offset = 0,
}: {
    orientation: GripOrientation;
    handleProps: HandleProps;
    offset?: GripOffset;
}) {
    const { style: handleStyle, ...restHandleProps } = handleProps;
    const isVertical = orientation === "vertical";
    const layout = GRIP_LAYOUT[orientation];

    return (
        <div
            className={cn("group flex shrink-0 items-center justify-center", {
                "flex-col": !isVertical,
            })}
            style={{
                [isVertical ? "marginTop" : "marginLeft"]: offset,
                padding: isVertical
                    ? `${layout.lengthPaddingPx}px ${layout.crossPaddingPx}px`
                    : `${layout.crossPaddingPx}px ${layout.lengthPaddingPx}px`,
                ...(isVertical ? { width: gripCrossSizePx("vertical") } : {}),
                ...handleStyle,
            }}
            {...restHandleProps}
        >
            <div className={cn("flex items-center justify-center gap-0.5", { "flex-col": !isVertical })}>
                <GripBar orientation={orientation} />
                <GripBar orientation={orientation} />
            </div>
        </div>
    );
}
